import React from 'react'
import { Card } from './ui/card'
import { Badge } from './ui/badge'
import { Button } from './ui/button'
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar'
import { MessageSquare, ThumbsUp, ThumbsDown, ShieldCheck, AlertCircle } from 'lucide-react'
import LinkPreviewCard from './LinkPreviewCard'
import { useNavigate } from 'react-router-dom'
import { formatNumber } from '../utils/formatNumber'

export default function FeedCard({ 
  transaction, 
  currentWallet, 
  onVote, 
  onDiscuss 
}) {
  const navigate = useNavigate()
  const isPending = transaction.status === 'pending'
  const isApproved = transaction.status === 'approved'
  const canVote = isPending && !transaction.voters?.includes(currentWallet)
  const isParticipant = 
    currentWallet === transaction.from_wallet || 
    currentWallet === transaction.to_wallet
  
  const approvalsCount = transaction.approvals?.length || 0
  const rejectionsCount = transaction.rejections?.length || 0
  
  // Check if discussion has new activity (last 24 hours)
  const hasNewActivity = () => {
    if (!transaction.latest_discussion_at) return false
    const messageTime = new Date(transaction.latest_discussion_at)
    const now = new Date()
    const hoursDiff = (now - messageTime) / (1000 * 60 * 60)
    return hoursDiff < 24
  }
  
  const getInitials = (name) => {
    if (!name) return '??'
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
  }
  
  return (
    <Card className="p-3 sm:p-4 hover:shadow-md transition-shadow dark:bg-gray-800 dark:border-gray-700">
      {/* Status Banner */}
      <div className={`mb-3 px-2 sm:px-3 py-1 rounded inline-block text-xs sm:text-sm font-medium ${
        isPending ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' :
        isApproved ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
        'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
      }`}>
        {isPending ? '⏳ Pending Votes' :
         isApproved ? '✅ Approved' :
         '❌ Rejected'}
      </div>
      
      {/* Transaction Details */}
      <div className="mb-3">
        {/* Respect Amount and Recipient - Mobile Optimized */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-3 bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg">
          {/* PR Amount */}
          <div className={`text-2xl sm:text-3xl font-bold ${
            transaction.respect_amount > 0 
              ? 'text-green-600 dark:text-green-400' 
              : 'text-red-600 dark:text-red-400'
          }`}>
            {transaction.respect_amount > 0 ? '+' : ''}{formatNumber(transaction.respect_amount)} PR
          </div>
          
          {/* Arrow and Recipient */}
          <div className="flex items-center gap-2">
            <span className="text-gray-400 dark:text-gray-500 text-lg hidden sm:inline">→</span>
            <span className="text-gray-500 dark:text-gray-400 text-sm sm:hidden">to</span>
            
            {/* To User */}
            <Avatar className="w-8 h-8 sm:w-10 sm:h-10 border-2 border-gray-200 dark:border-gray-600">
              {transaction.to_profile_image ? (
                <AvatarImage src={transaction.to_profile_image} alt={transaction.to_name} />
              ) : (
                <AvatarFallback className="font-bold bg-gradient-to-br from-purple-500 to-pink-500 text-white text-xs sm:text-sm">
                  {getInitials(transaction.to_name)}
                </AvatarFallback>
              )}
            </Avatar>
            <div>
              <div className="flex items-center gap-1">
                <span 
                  className="font-semibold text-sm sm:text-base text-indigo-600 dark:text-indigo-400 cursor-pointer hover:underline"
                  onClick={() => navigate(`/profile/${transaction.to_username}`)}
                >
                  {transaction.to_username || 'Unknown'}
                </span>
                {/* Profile status badges - UNCLAIMED only hides when VERIFIED */}
                {transaction.to_is_claimable && transaction.to_verified !== true ? (
                  transaction.to_is_claimed ? (
                    <div className="flex items-center gap-0.5 bg-orange-500 px-1.5 py-0.5 rounded" title="Pending Claim Verification">
                      <AlertCircle className="w-3 h-3 text-white flex-shrink-0" />
                      <span className="text-xs text-white font-bold">CLAIMED</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-0.5 bg-yellow-500 px-1.5 py-0.5 rounded" title="Unclaimed Profile">
                      <AlertCircle className="w-3 h-3 text-white flex-shrink-0" />
                      <span className="text-xs text-white font-bold">UNCLAIMED</span>
                    </div>
                  )
                ) : transaction.to_verified ? (
                  <ShieldCheck className="w-4 h-4 text-green-500 flex-shrink-0" title="Verified" />
                ) : (
                  <AlertCircle className="w-4 h-4 text-orange-500 flex-shrink-0" title="Unverified - Be cautious" />
                )}
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {transaction.to_name}
              </span>
            </div>
          </div>
        </div>
        
        {/* Pledge and Reward Info */}
        {isPending && (
          <div className="flex flex-wrap gap-2 sm:gap-4 mb-3 text-xs sm:text-sm">
            <span className="text-gray-600 dark:text-gray-400">
              <span className="font-medium">Pledge:</span>{' '}
              <span className="text-orange-600 dark:text-orange-400 font-semibold">
                {formatNumber(transaction.pledge_amount || Math.abs(Math.round(transaction.respect_amount * 0.12)))} PR
              </span>
            </span>
            <span className="text-gray-600 dark:text-gray-400">
              <span className="font-medium">Voter Reward Pool:</span>{' '}
              <span className="text-green-600 dark:text-green-400 font-semibold">
                {formatNumber(transaction.voter_reward_pool || Math.abs(Math.round(transaction.respect_amount * 0.10)))} PR
              </span>
            </span>
          </div>
        )}
        
        {/* Reason - Bold, not italic, no quotes */}
        <p className="text-sm sm:text-base text-gray-800 dark:text-gray-200 mb-3 font-semibold">
          {transaction.reason}
        </p>
        
        {/* Categories */}
        {transaction.categories && transaction.categories.length > 0 && (
          <div className="flex flex-wrap gap-1 sm:gap-2 mb-3">
            {transaction.categories.map(cat => (
              <Badge key={cat} variant="outline" className="text-xs">{cat}</Badge>
            ))}
          </div>
        )}
        
        {/* Link Preview */}
        {transaction.link && transaction.link_platform && (
          <div className="mb-3">
            <LinkPreviewCard
              url={transaction.link}
              platform={transaction.link_platform}
            />
          </div>
        )}
        
        {/* Timestamp */}
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {new Date(transaction.created_at).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })}
        </span>
      </div>
      
      {/* Vote Counts - Always show for pending */}
      {isPending && (
        <div className="flex items-center gap-3 sm:gap-4 mb-3 p-2 bg-gray-50 dark:bg-gray-700/50 rounded">
          <div className="flex items-center gap-1">
            <ThumbsUp className="w-4 h-4 text-green-600 dark:text-green-400" />
            <span className="text-xs sm:text-sm font-medium text-green-600 dark:text-green-400">
              Approvals: {approvalsCount}/10
            </span>
          </div>
          <div className="flex items-center gap-1">
            <ThumbsDown className="w-4 h-4 text-red-600 dark:text-red-400" />
            <span className="text-xs sm:text-sm font-medium text-red-600 dark:text-red-400">
              Rejections: {rejectionsCount}/10
            </span>
          </div>
        </div>
      )}
      
      {/* Actions */}
      <div className="flex items-center gap-2 pt-3 border-t dark:border-gray-700 flex-wrap">
        {isPending && (
          <>
            {canVote && !isParticipant && (
              <>
                <Button
                  size="sm"
                  onClick={() => onVote(transaction.id, 'approve')}
                  className="bg-green-600 hover:bg-green-700 text-white text-xs sm:text-sm"
                >
                  <ThumbsUp className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                  Approve
                </Button>
                <Button
                  size="sm"
                  onClick={() => onVote(transaction.id, 'reject')}
                  className="bg-red-600 hover:bg-red-700 text-white text-xs sm:text-sm"
                >
                  <ThumbsDown className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                  Reject
                </Button>
              </>
            )}
            
            {isParticipant && (
              <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 italic">
                You are a participant
              </span>
            )}
            
            {!canVote && !isParticipant && (
              <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 italic">
                Already voted
              </span>
            )}
            
            {!isParticipant && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onDiscuss(transaction.id)}
                className="relative ml-auto text-xs sm:text-sm"
              >
                <MessageSquare className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                Discuss
                {hasNewActivity() && (
                  <Badge 
                    className="absolute -top-1 -right-1 bg-red-500 text-white text-xs px-1 py-0"
                  >
                    New
                  </Badge>
                )}
                {transaction.discussion_count > 0 && (
                  <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">
                    ({transaction.discussion_count})
                  </span>
                )}
              </Button>
            )}
          </>
        )}
        
        {/* Show completed status info */}
        {!isPending && (
          <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
            Final votes - Approvals: {approvalsCount}, Rejections: {rejectionsCount}
          </span>
        )}
      </div>
    </Card>
  )
}
